import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import { Bones } from '@simplywallst/ui-core';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { batchFetchPortfolio } from '@/components/DeprecatedPortfolio/redux/routines';

import useFetchEveryThirty from '../../hooks/useFetchEveryThirty';
import { Card, Name, Summary, Returns, BoneWrapper, Details } from './styled';

export interface Props {
  id: string;
  name: string;
  index: number;
  canonicalUrl: string;
  lastAnalysisUpdate: number | null;
  isWatchlist: boolean;
  portfolioWithNoTransactions: boolean;
  dateGenerated: string;
  holdingsCount: string;
  return7D: number | null;
  return7DFormatted: string;
  totalValueFormatted: string;
  totalGainFormatted: string;
  totalDividendReturn: number | null;
  totalDividendReturnFormatted: string;
}

interface BoneProps {
  fetchStatus: ReturnType<typeof useFetchEveryThirty>;
  children: NonNullable<ReactNode>;
}

const BonesIfLoading = ({ children, fetchStatus }: BoneProps) => {
  if (fetchStatus === 'fetching') {
    return (
      <BoneWrapper>
        <Bones />
      </BoneWrapper>
    );
  }

  return <>{children}</>;
};

const DeprecatedPortfolioItem = ({
  id,
  name,
  index,
  canonicalUrl,
  lastAnalysisUpdate,
  isWatchlist,
  totalValueFormatted,
  portfolioWithNoTransactions,
  return7D,
  return7DFormatted,
  holdingsCount,
  totalDividendReturn,
  totalDividendReturnFormatted,
}: Props) => {
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();
  const handleTracking = useCallback(() => {
    trackEvent({
      action: 'click',
      modifier: 'portfolio_list_item',
      portfolioId: id,
      index,
    });
  }, [id, index, trackEvent]);
  const callback = useCallback(() => {
    dispatch(batchFetchPortfolio.trigger({ id }));
  }, [dispatch, id]);
  const fetchStatus = useFetchEveryThirty(lastAnalysisUpdate, callback);

  return (
    <Card>
      <RouterLink to={canonicalUrl} onClick={handleTracking} noUnderline>
        <Details>
          <Name>{name}</Name>
          <BonesIfLoading fetchStatus={fetchStatus}>
            <Summary>
              {isWatchlist ? holdingsCount : totalValueFormatted}
            </Summary>
          </BonesIfLoading>
        </Details>
        <Returns>
          <li>
            <span>7D</span>
            <BonesIfLoading fetchStatus={fetchStatus}>
              <PositiveNegativeValue value={return7D}>
                {return7DFormatted}
              </PositiveNegativeValue>
            </BonesIfLoading>
          </li>
          <li>
            <span>
              {!isWatchlist && !portfolioWithNoTransactions ? 'Total' : '1Y'}
            </span>
            <BonesIfLoading fetchStatus={fetchStatus}>
              <PositiveNegativeValue value={totalDividendReturn}>
                {totalDividendReturnFormatted}
              </PositiveNegativeValue>
            </BonesIfLoading>
          </li>
        </Returns>
      </RouterLink>
    </Card>
  );
};

export default DeprecatedPortfolioItem;
